/**
 * SidebarContext — steuert das Mobile-Sidebar-Sheet (vaul-Drawer).
 *
 * Im Desktop-Modus (md+) ist die Sidebar permanent sichtbar — der Context
 * wird dann nicht aktiv genutzt. Auf Mobile öffnet der Topbar-Hamburger
 * via setOpen(true) das vaul-Sheet, das die normale Sidebar wrappt.
 *
 * Pattern:
 *   const { open, setOpen, toggle } = useSidebar();
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

interface SidebarContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  const value = useMemo<SidebarContextValue>(
    () => ({ open, setOpen, toggle }),
    [open, toggle],
  );
  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    // Defensive: Wenn jemand den Hook ausserhalb des Providers nutzt, liefere
    // einen No-Op-Stub statt zu werfen. So bleibt die Desktop-Sidebar funktional.
    return { open: false, setOpen: () => {}, toggle: () => {} };
  }
  return ctx;
}
